import {
  AddonInfo,
  JiraContext,
  JiraEpic,
  JiraIssue,
  JiraUser,
  JiraWorklog,
} from '../types';
import { ADD_WORKLOG_ERROR_REASONS } from '../utils/jiraWorklogs';

import { getErrorMessage } from './helpers';

interface AddonProperty<T> {
  key: string;
  value: T;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraProjectsResponse {
  maxResults: number;
  startAt: number;
  total: number;
  isLast: boolean;
  values: JiraProject[];
}

export const getContext = async () =>
  (await AP.context.getContext()) as JiraContext;

export const ADDON_PROPERTY_ISSUE_SIDEBAR = 'addon-issue-sidebar';

export async function setAddonProperty<T>(
  propertyName: string,
  data: T
): Promise<T> {
  return await AP.request({
    url: `/rest/atlassian-connect/1/addons/toggl/properties/${propertyName}`,
    type: 'PUT',
    data: JSON.stringify(data),
    contentType: 'application/json',
  }).then(() => {
    showFlag('Success', 'Value is saved', 'success');
    return data;
  });
}

export async function deleteAddonProperty(propertyName: string) {
  await AP.request({
    url: `/rest/atlassian-connect/1/addons/toggl/properties/${propertyName}`,
    type: 'DELETE',
  }).then(() => {
    showFlag('Success', 'Value is saved', 'success');
  });
}

export async function getAddonProperty<T>(
  propertyName: string,
  defaultValue: unknown
) {
  try {
    const result = await AP.request({
      url: `/rest/atlassian-connect/1/addons/toggl/properties/${propertyName}`,
      type: 'GET',
      contentType: 'application/json',
    });
    const property = JSON.parse(result.body) as AddonProperty<T>;
    return property.value;
  } catch (error) {
    if ((error as JiraApiError).xhr.status !== 404) {
      console.error(
        `Unexpected error while getting addon property - ${propertyName}`,
        error
      );
      throw error;
    }
    return defaultValue;
  }
}

export function showFlag(
  title: string,
  body: string,
  type: 'info' | 'success' | 'warning' | 'error',
  close: 'manual' | 'auto' = 'auto'
) {
  AP.flag.create({
    title,
    body: body || '',
    type: type || 'success',
    close,
  });
}

export async function getProjects({
  startAt = 0,
  maxResults = 50,
  query = '',
}: {
  startAt?: number;
  maxResults?: number;
  query: string;
}): Promise<JiraProjectsResponse> {
  const result = await AP.request(
    `/rest/api/3/project/search?expand=description&startAt=${startAt}&maxResults=${maxResults}&query=${query}`
  );
  return JSON.parse(result.body) as JiraProjectsResponse;
}

export function isJiraAdmin() {
  const params = new URLSearchParams(location.search);
  return params.get('isAdmin') === 'true';
}

export async function getJiraIssueById(issueId: string | number) {
  const result = await AP.request(
    `/rest/api/3/issue/${issueId}?fields=summary,labels,issuetype,parent`
  );
  return JSON.parse(result.body) as JiraIssue;
}

function getLocation(): Promise<string> {
  return new Promise((resolve, reject) => {
    AP.getLocation((url) => {
      if (url) {
        resolve(url);
      } else {
        reject(new Error('Error getting location'));
      }
    });
  });
}

export async function getBaseUrl() {
  try {
    const currentJiraLocation = await getLocation();
    return new URL(currentJiraLocation).origin;
  } catch (e) {
    const params = new URLSearchParams(location.search);
    return params.get('xdm_e') || '/';
  }
}

export async function getBulkUsers({
  accountIds,
  maxResults = 20,
}: {
  accountIds: string[];
  maxResults?: number;
}) {
  const ids = accountIds.map((id) => ['accountId', id]);
  const query = new URLSearchParams([
    ...ids,
    ['maxResults', maxResults.toString()],
  ]).toString();
  const result = await AP.request(`/rest/api/3/user/bulk?${query}`);
  return JSON.parse(result.body) as { values: JiraUser[] };
}

export async function getIssueWorklogs(issueKey: string) {
  try {
    const result = await AP.request(`/rest/api/2/issue/${issueKey}/worklog`);
    return (
      (JSON.parse(result.body) as { worklogs: JiraWorklog[] })?.worklogs ?? []
    );
  } catch (error) {
    if ((error as JiraApiError)?.xhr?.status === 404) {
      return { error: ADD_WORKLOG_ERROR_REASONS.WRONG_ISSUE_KEY };
    }
    return { error: ADD_WORKLOG_ERROR_REASONS.UNEXPECTED_ERROR };
  }
}

export interface CreateWorklogOption {
  timeSpentSeconds: number;
  comment: string;
  started: string;
  issueKey: string;
  togglTimeZone: string;
  jiraTimeZone?: string;
}

export async function createJiraWorklog({
  issueKey,
  timeSpentSeconds,
  comment,
  started,
}: CreateWorklogOption) {
  try {
    const result = await AP.request({
      url: `/rest/api/2/issue/${issueKey}/worklog`,
      type: 'POST',
      data: JSON.stringify({
        comment,
        started,
        timeSpentSeconds,
      }),
      contentType: 'application/json',
    });
    return JSON.parse(result.body) as JiraWorklog;
  } catch (error) {
    console.error('Error while creating jira worklog', getErrorMessage(error));
    return ADD_WORKLOG_ERROR_REASONS.UNEXPECTED_ERROR;
  }
}

interface JiraApiError {
  err: string;
  xhr: {
    status: number;
  };
}

export async function deleteWorklog(issueKey: string, worklogId: string) {
  try {
    await AP.request({
      url: `/rest/api/3/issue/${issueKey}/worklog/${worklogId}`,
      type: 'DELETE',
    });
  } catch (error) {
    if ((error as JiraApiError)?.xhr?.status === 404) {
      console.warn('Worklog already removed');
    } else {
      throw error;
    }
  }
}

export async function getCurrentIssue() {
  const { jira } = await getContext();

  return await getJiraIssueById(jira.issue.key);
}

export async function getEpicById(epicId: number) {
  try {
    const result = await AP.request(`/rest/agile/1.0/epic/${epicId}`);
    return JSON.parse(result.body) as JiraEpic;
  } catch (error) {
    const issue = await getJiraIssueById(epicId);
    return { summary: issue.fields.summary } as JiraEpic;
  }
}

export async function getPluginInfo(addonKey: string) {
  const result = await AP.request(
    `/rest/atlassian-connect/1/addons/${addonKey}`
  );
  return JSON.parse(result.body) as AddonInfo;
}

export function getInstanceUrlWithCallback(callback: (url: string) => unknown) {
  AP.getLocation((location) => {
    const instanceUrl = new URL(location);
    callback(instanceUrl.origin);
  });
}
